"use client";

import * as Sentry from "@sentry/nextjs";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showCert, setShowCert] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Not awaited, so the confirmation below is optimistic. The catch is what
    // keeps a dropped connection from surfacing as a stackless "Load failed"
    // unhandled rejection that names no call site.
    fetch("/api/form/newsletter", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
      // The visitor tends to leave as soon as the confirmation shows —
      // most of them arrive in Facebook's in-app browser, which tears the
      // webview down on close. keepalive lets the request outlive the page.
      keepalive: true,
    }).catch((error) => {
      // Only a warning: a transport error here does not mean the request
      // was lost. The webview can cut the connection after the server has
      // the body (it did — Sentry TRAVELALVARO-SK-8W shows "Email sent" in
      // the same trace), and the route reports its own failures at error.
      Sentry.captureException(error, {
        level: "warning",
        tags: { source: "newsletter-signup", fetch_url: "/api/form/newsletter" },
      });
    });

    setSubmitted(true);
    setEmail("");
  };

  return (
    <footer className="w-full text-white">
      <Image
        src="/assets/footer-background.svg"
        alt="footer background"
        width={1300}
        height={400}
        className="w-full"
      />
      <div className="bg-[#00638A] flex flex-col mx-auto justify-center items-center">
        <div className="grid lg:mt-4 max-w-86.25 md:max-w-262 mb-6 lg:gap-x-10 gap-7 gap-y-7 mx-5 md:mx-6 grid-cols-4">
          <HeadSection />
          <NewsletterSection
            email={email}
            setEmail={setEmail}
            submitted={submitted}
            handleSubmit={handleSubmit}
          />
          <ContactSection />
          <DocumentSection />
          <CertificateSection setShowCert={setShowCert} />
        </div>
        <div className="mb-4 flex flex-col md:flex-row gap-10 items-center justify-center">
          <Image
            src="/assets/union.svg"
            alt="Partner logo"
            width={120}
            height={42}
          />
          <Image
            src="/assets/respect.svg"
            alt="Respect logo"
            width={150}
            height={42}
          />
        </div>
      </div>
      <p className="text-center px-6 py-2 text-sm bg-[#005475]">
        © 2025, Travel Alvaro s.r.o., Všetky práva vyhradené. Prečítajte si{" "}
        <Link
          href="/dokumenty-na-stiahnutie"
          className="underline text-orange-400"
        >
          zmluvné podmienky, ochrana osobných údajov a ďalšie informácie.
        </Link>
      </p>
      {showCert && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center"
          onClick={() => setShowCert(false)}
        >
          <button
            className="absolute top-4 right-4 text-white text-3xl font-bold"
            onClick={(e) => {
              e.stopPropagation();
              setShowCert(false);
            }}
          >
            ×
          </button>
          <Image
            src="/assets/certificate.png"
            width={800}
            height={600}
            alt="SACKA Certificate Large"
            className="max-w-[90%] max-h-[90%] rounded-xl"
          />
        </div>
      )}
    </footer>
  );
}

function HeadSection() {
  return (
    <div className="col-span-4 lg:mt-0 mt-10 lg:col-span-2 flex flex-col gap-4 px-2.5">
      <h3 className="text-3xl font-bold font-kalam">Travel Alvaro</h3>
      <p className="text-sm md:max-w-112.5">
        Dobrodružná cestovná kancelária - <br className="md:hidden" />
        do sveta a ešte ďalej.
        <br />
        Cestuj po svete za super ceny v doprovode skvelých slovenských delgátov.
      </p>
      <div className="flex gap-2.5">
        <a
          href="https://www.facebook.com/profile.php?id=61572937145115"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image
            src="/assets/facebook-white.svg"
            alt="Facebook Logo"
            width={35}
            height={35}
          />
        </a>
        <a
          href="https://www.instagram.com/travel_alvaro/ "
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image
            src="/assets/instagram-white.svg"
            alt="Instagram Logo"
            width={35}
            height={35}
          />
        </a>
      </div>
    </div>
  );
}

function ContactSection() {
  return (
    <div className="col-span-4 md:col-span-2 lg:col-span-1 order-5 lg:order-0 p-2.5 h-41.25 leading-tight flex flex-col justify-between rounded-xl text-sm bg-[#FFFFFF1A]">
      <span className="font-bold">Kontakt</span>

      <div className="flex flex-col gap-3">
        <span className="block">
          Travel Alvaro s. r. o. <br />
          Priemyselná 831/6A<br />
          924 01 Galanta <br />
        </span>

        <span className="block">
          <a href="mailto:rezervacie@travelalvaro.sk" className="font-bold">
            rezervacie@travelalvaro.sk
          </a>
          <a href="tel:+421944408135" className="block font-bold">
            0944 408 135
          </a>
        </span>
      </div>
    </div>
  );
}

function DocumentSection() {
  return (
    <div className="col-span-4 overflow-hidden md:col-span-2 lg:col-span-1 order-4 lg:order-0 p-2.5 h-76 lg:h-41.25 rounded-xl text-sm bg-[#FFFFFF1A] flex flex-col">
      <span className="font-bold">Garančný list</span>
      <Link
        className="cursor-pointer rounded-[10px] relative block flex-1 min-h-0 mt-1"
        href="https://cms.travelalvaro.sk/media/pages/dokumenty/42f7e20a89-1772535904/garancny-list.pdf"
        target="_blank"
      >
        <Image
          src="/assets/garancny-list.png"
          fill
          alt="Garančný list"
          className="rounded-[10px] object-top object-cover"
        />
      </Link>
    </div>
  );
}

type NewsletterSectionProps = {
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  submitted: boolean;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
};

function NewsletterSection({
  email,
  setEmail,
  submitted,
  handleSubmit,
}: NewsletterSectionProps) {
  return (
    <div className="col-span-4 lg:col-span-2 flex gap-6.5 flex-col px-2.5 text-sm">
      <h4 className="text-[32px] font-bold font-kalam">Tipy na výlety</h4>

      {submitted ? (
        <p className="mt-10 text-white font-semibold">
          Ďakujeme za prihlásenie na odber! ✨
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="flex gap-5 flex-col">
          <div className="relative flex items-center">
            <input
              type="email"
              placeholder="tvoj@email.sk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 py-3 px-8 bg-white rounded-full w-full text-black shadow-md"
            />
            <button
              type="submit"
              className="absolute right-0 hover:bg-[#619F1C] cursor-pointer top-1/2 transform -translate-y-1/2 bg-[#D27140] z-10 text-white px-6 h-12 rounded-full font-bold shadow-lg"
            >
              <span className="hidden md:block">Chcem notifikácie</span>
              <Image
                src="/assets/Icon-plane.svg"
                alt="Send"
                width={20}
                height={20}
                className="block md:hidden mx-2.5"
              />
            </button>
          </div>
          <div className="flex items-start gap-2 text-xs md:text-center lg:justify-start">
            <input
              id="terms"
              type="checkbox"
              required
              className="appearance-none h-4 w-4 border border-gray-400 bg-white rounded-full checked:bg-[#00638A] checked:border-white checked:border-[3px] focus:outline-none shrink-0"
            />
            <label htmlFor="terms" className="text-left">
              Súhlasím so spracovaním{" "}
              <Link href="/dokumenty-na-stiahnutie" className="text-orange-400">
                osobných údajov
              </Link>{" "}
              a
              <Link href="/dokumenty-na-stiahnutie" className="text-orange-400">
                {" "}
                obchodnými podmienkami
              </Link>
              .
            </label>
          </div>
        </form>
      )}
    </div>
  );
}

type CertificateSectionProps = {
  setShowCert: React.Dispatch<React.SetStateAction<boolean>>;
};

function CertificateSection({ setShowCert }: CertificateSectionProps) {
  return (
    <div className="col-span-4 lg:col-span-2 order-3 lg:order-0 p-2.5 lg:h-41.25 flex-col lg:flex-row justify-center lg:items-start lg:justify-between gap-7 lg:gap-2.5  leading-tight flex rounded-xl text-sm bg-[#FFFFFF1A]">
      <div className="max-w-63.5">
        <span className="font-bold">
          Člen Slovenskej asociácie cestovných kancelárií a cestovných agentúr
          (SACKA)
        </span>
        <br />
        <br />
        <span className="text-xs">
          Členstvo bolo schválené 4.11.2025 s účinnosťou od 1.2.2026.
        </span>
      </div>
      <div className="cursor-pointer" onClick={() => setShowCert(true)}>
        <Image
          src="/assets/certificate.png"
          width={200}
          height={145}
          alt="SACKA Certificate"
          className="rounded-[10px] md:m-auto mx-auto w-75 md:w-50"
        />
      </div>
    </div>
  );
}
